import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as authApi from '../services/authApi';
import type { RegisterPendingResponse } from '../services/authApi';
import { profileApi } from '../services/profileApi';
import { submitKYC as submitKYCApi } from '../services/kycApi';
import { buildStaticUrl } from '../utils/imageUrl';
import { normalizeProfileType, type ProfileType } from '../utils/profileType';
import { normalizeSubscriptionLevel, type SubscriptionLevel } from '../utils/subscriptionUtils';
import {
  clearPersistedTenantMobileSession,
  clearTenantMobileLoginData,
  hasPersistedTenantMobileSession,
  isNativeMobileApp,
  MOBILE_APP_LOGIN_PATH,
  requestTenantQrLoginScreen,
  unlockMobileSession,
} from '../utils/mobileApp';
import { AUTH_EXPIRED_EVENT } from '../services/httpClient';

export type AccountType = ProfileType;

const KYC_TARGET_STORAGE_KEY = 'kyc_target_account_type';

async function resolveVerifiedProviderTarget(): Promise<AccountType | null> {
  try {
    const stored = localStorage.getItem(KYC_TARGET_STORAGE_KEY);
    if (stored === 'landlord' || stored === 'business') {
      return stored;
    }
  } catch (_) { /* ignore */ }

  try {
    const { getKYCStatus } = await import('../services/kycApi');
    const kyc = await getKYCStatus();
    if (kyc.is_verified && (kyc.target_account_type === 'landlord' || kyc.target_account_type === 'business')) {
      return kyc.target_account_type;
    }
  } catch (_) { /* ignore */ }

  return null;
}

export interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  banner?: string;
  phone?: string;
  address?: string;
  cccd?: string;
  dateOfBirth?: string;
  bio?: string;
  accountType: AccountType;
  availableAccountTypes?: AccountType[]; // Danh sách các account types mà user đã có (kể cả inactive)
  role: 'user' | 'admin' | 'agent';
  createdAt: Date;
  // Subscription fields
  subscriptionLevel?: SubscriptionLevel;
  isPremium?: boolean;
  isSubscriptionActive?: boolean;
  subscriptionEndDate?: Date;
  // Additional fields based on account type
  company?: string;
  website?: string;
  license?: string;
  taxId?: string;
  licenseNumber?: string;
  specialization?: string;
  experience?: string;
  businessType?: string;
  industry?: string;
  employeeCount?: string;
  bankAccount?: string;
  balance?: number;
  facebook?: string;
  instagram?: string;
  linkedin?: string;
  twitter?: string;
  line?: string;
  isVerified?: boolean;
  // SePay credentials (tab Ngân hàng)
  sepay_client_id?: string;
  sepay_api_key?: string;
  sepay_checksum_key?: string;
  bankhub_linked?: boolean;
  bankhub_brand_name?: string;
  bankhub_account_number?: string;
  bankhub_account_holder?: string;
  bankhub_bank_code?: string;
  beneficiary_status?: string | null;
}

// Define context type
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithAccessToken: (token: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  register: (data: RegisterData) => Promise<RegisterPendingResponse>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  switchAccount: (accountType: AccountType) => Promise<string>;
  submitKYC: (documents: any, accountType?: string) => Promise<any>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
  invalidateTenantSession: (message?: string) => Promise<void>;
}

interface RegisterData {
  email: string;
  password: string;
  name: string;
  accountType: AccountType;
  phone?: string;
  address?: string;
}

// subscription_level từ API — chuẩn hóa basic / standard / premium (hỗ trợ legacy pro/elite)

// helper to map backend user to UI user
function mapUser(u: authApi.UserResponse | any, accountType?: AccountType, profileData?: any): User {
  const userData = profileData || u;
  const mappedAccountType: AccountType = normalizeProfileType(
    userData.account_type || accountType
  );
  
  const availableAccountTypes: AccountType[] = (userData.available_account_types || u?.available_account_types || [])
    .map((type: string) => normalizeProfileType(type))
    .filter((type: AccountType, index: number, arr: AccountType[]) => arr.indexOf(type) === index);
  
  // subscription_level: ưu tiên profile/me (thường đầy đủ hơn auth/me), hỗ trợ nhiều key và chuẩn hóa giá trị
  const rawLevel = profileData?.subscription_level ?? profileData?.subscriptionLevel
    ?? u?.subscription_level ?? u?.subscriptionLevel
    ?? userData?.subscription_level ?? userData?.subscriptionLevel;
  const subscriptionLevel = normalizeSubscriptionLevel(rawLevel);
  const rawEnd = profileData?.subscription_end_date ?? u?.subscription_end_date ?? userData?.subscription_end_date;
  const subscriptionEndDate = rawEnd ? new Date(rawEnd) : undefined;
  const validEndDate = subscriptionEndDate && !Number.isNaN(subscriptionEndDate.getTime())
    ? subscriptionEndDate
    : undefined;
  const apiActive = profileData?.is_subscription_active ?? u?.is_subscription_active ?? userData?.is_subscription_active;
  const isSubscriptionActive = apiActive === true || (
    subscriptionLevel !== 'free' && (!validEndDate || validEndDate.getTime() > Date.now())
  );

  const avatarUrl = buildStaticUrl(userData.avatar);
  const bannerUrl = buildStaticUrl(userData.banner);
  
  return {
    id: String(userData.id || u?.id),
    email: profileData?.email || userData.email || u?.email,
    name: profileData?.full_name || userData.full_name || u?.full_name || userData.username || u?.username,
    phone: profileData?.phone ?? u?.phone ?? undefined,
    address: profileData?.address ?? u?.address ?? undefined,
    cccd: profileData?.cccd ?? u?.cccd ?? undefined,
    dateOfBirth: profileData?.date_of_birth ?? u?.dateOfBirth ?? undefined,
    bio: profileData?.bio ?? u?.bio ?? undefined,
    company: profileData?.company ?? u?.company ?? undefined,
    website: profileData?.website ?? u?.website ?? undefined,
    license: profileData?.license ?? profileData?.license_number ?? u?.license ?? undefined,
    taxId: profileData?.tax_id ?? u?.taxId ?? undefined,
    licenseNumber: profileData?.license_number ?? u?.licenseNumber ?? undefined,
    specialization: profileData?.specialization ?? u?.specialization ?? undefined,
    experience: profileData?.experience ?? u?.experience ?? undefined,
    businessType: profileData?.business_type ?? u?.businessType ?? undefined,
    industry: profileData?.industry ?? u?.industry ?? undefined,
    employeeCount: profileData?.employee_count ?? u?.employeeCount ?? undefined,
    facebook: profileData?.facebook ?? u?.facebook ?? undefined,
    instagram: profileData?.instagram ?? u?.instagram ?? undefined,
    linkedin: profileData?.linkedin ?? u?.linkedin ?? undefined,
    twitter: profileData?.twitter ?? u?.twitter ?? undefined,
    line: profileData?.line ?? u?.line ?? undefined,
    isVerified: profileData?.is_verified ?? u?.isVerified ?? false,
    avatar: avatarUrl,
    banner: bannerUrl,
    accountType: mappedAccountType,
    availableAccountTypes: availableAccountTypes.length > 0 ? availableAccountTypes : undefined,
    subscriptionLevel,
    isPremium: isSubscriptionActive,
    isSubscriptionActive,
    subscriptionEndDate: validEndDate,
    role: 'user' as const,
    createdAt: new Date(userData.created_at || u?.created_at),
    sepay_client_id: profileData?.sepay_client_id ?? u?.sepay_client_id ?? undefined,
    sepay_api_key: profileData?.sepay_api_key ?? u?.sepay_api_key ?? undefined,
    sepay_checksum_key: profileData?.sepay_checksum_key ?? u?.sepay_checksum_key ?? undefined,
    bankhub_linked: Boolean(profileData?.bankhub_linked ?? u?.bankhub_linked),
    bankhub_brand_name: profileData?.bankhub_brand_name ?? u?.bankhub_brand_name ?? undefined,
    bankhub_account_number: profileData?.bankhub_account_number ?? u?.bankhub_account_number ?? undefined,
    bankhub_account_holder: profileData?.bankhub_account_holder ?? u?.bankhub_account_holder ?? undefined,
    bankhub_bank_code: profileData?.bankhub_bank_code ?? u?.bankhub_bank_code ?? undefined,
    beneficiary_status: profileData?.beneficiary_status ?? u?.beneficiary_status ?? undefined,
  };
}

// Create context
const AuthContext = createContext<AuthContextType | null>(null);

// Create provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Check for existing session
  useEffect(() => {
    const checkAuth = async () => {
      // Trang bridge nhận JWT từ app — không kiểm tra token cũ (tránh xóa token mới)
      if (window.location.pathname.startsWith('/auth/mobile-session')) {
        setIsLoading(false);
        return;
      }

      try {
        const token = authApi.getToken();
        console.log('Checking auth with token:', token ? `${token.substring(0, 20)}...` : 'No token');
        
        if (token) {
          // Token validation removed - Backend validates every request
          // If token is invalid/expired, authApi.me() will throw 401 error
          // Error is caught by interceptor which logs out user
          
          const me = await authApi.me();
          console.log('Auth successful, user data:', me);
          try {
            const profile = await profileApi.getProfile();
            const mapped = mapUser(me, undefined, profile);
            if (isNativeMobileApp()) {
              if (mapped.accountType === 'tenant') {
                if (!hasPersistedTenantMobileSession()) {
                  authApi.clearToken();
                  setUser(null);
                  return;
                }
                try {
                  const { checkTenantQrSession } = await import('../services/tenantQrAuthApi');
                  const status = await checkTenantQrSession();
                  if (!status.valid) {
                    clearTenantMobileLoginData(mapped.id);
                    authApi.clearToken();
                    setUser(null);
                    requestTenantQrLoginScreen();
                    setError(
                      status.message ||
                        'Phiên đăng nhập đã hết hạn. Vui lòng nhập số căn cước công dân lại.',
                    );
                    return;
                  }
                } catch (sessionErr) {
                  console.warn('Tenant QR session check skipped:', sessionErr);
                }
                // Chờ bấm "Tiếp tục với Khách thuê" — không tự vào app.
              }
              setUser(mapped);
              return;
            }
            const isVerified = mapped.isVerified;
            const needsSwitch = mapped.accountType === 'tenant' && isVerified && !isNativeMobileApp();
            if (needsSwitch) {
              const target = await resolveVerifiedProviderTarget();
              if (target) {
                try {
                  await authApi.switchAccount(target);
                  localStorage.removeItem(KYC_TARGET_STORAGE_KEY);
                  const freshMe = await authApi.me();
                  const freshProfile = await profileApi.getProfile();
                  setUser(mapUser(freshMe, target, freshProfile));
                  return;
                } catch (switchErr) {
                  console.warn('Auto switch after verified KYC failed:', switchErr);
                }
              }
            }
            setUser(mapped);
          } catch (_) {
            setUser(mapUser(me));
          }
        }
      } catch (err: any) {
        console.error('Auth check failed:', err);
        console.error('Error details:', {
          message: err.message,
          stack: err.stack,
          response: err.response
        });

        if (isNativeMobileApp()) {
          const msg = err?.message || '';
          const authFailed =
            /401|unauthorized|token không hợp lệ|session expired|authentication failed/i.test(msg) ||
            err?.response?.status === 401;
          if (authFailed) {
            clearPersistedTenantMobileSession();
            authApi.clearToken();
          }
          setUser(null);
          return;
        }

        const pathname = window.location.pathname;
        const onAdminSurface =
          pathname === '/home' ||
          pathname.startsWith('/admin');

        // Clear invalid user token, but do not kick an admin off /home or /admin.
        authApi.clearToken();
        if (
          !onAdminSurface &&
          !pathname.startsWith('/auth/login') &&
          !pathname.startsWith('/auth/mobile-session') &&
          !pathname.startsWith('/mobile/login')
        ) {
          navigate('/auth/login', { replace: true });
        }
        
        if (err.message?.includes('Token không hợp lệ') || err.message?.includes('Unauthorized')) {
          setError('Session expired. Please login again.');
        } else if (err.message?.includes('No token')) {
          setError('Please login to continue.');
        } else {
          setError('Authentication failed. Please login again.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    const handleAuthExpired = () => {
      if (isNativeMobileApp()) {
        clearPersistedTenantMobileSession();
        authApi.clearToken();
        setUser(null);
        setIsLoading(false);
        navigate(MOBILE_APP_LOGIN_PATH, { replace: true });
        return;
      }
      setUser(null);
      setIsLoading(false);
      const pathname = window.location.pathname;
      if (pathname === '/home' || pathname.startsWith('/admin')) {
        return;
      }
      const loginPath = '/auth/login';
      if (!pathname.startsWith(loginPath)) {
        navigate(loginPath, { replace: true });
      }
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
  }, [navigate]);

  const login = useCallback(async (email: string, password: string) => {
    try {
      setIsLoading(true);
      setError(null);
      if (isNativeMobileApp()) {
        clearPersistedTenantMobileSession();
        authApi.clearToken();
      }
      await authApi.login(email, password);
      const me = await authApi.me();
      try {
        const profile = await profileApi.getProfile();
        let mapped = mapUser(me, undefined, profile);
        if (
          isNativeMobileApp() &&
          mapped.accountType === 'tenant' &&
          mapped.availableAccountTypes?.includes('landlord')
        ) {
          await authApi.switchAccount('landlord');
          const freshMe = await authApi.me();
          const freshProfile = await profileApi.getProfile();
          mapped = mapUser(freshMe, 'landlord', freshProfile);
        }
        setUser(mapped);
      } catch (_) {
        let mapped = mapUser(me);
        if (
          isNativeMobileApp() &&
          mapped.accountType === 'tenant' &&
          mapped.availableAccountTypes?.includes('landlord')
        ) {
          await authApi.switchAccount('landlord');
          const freshMe = await authApi.me();
          mapped = mapUser(freshMe, 'landlord');
        }
        setUser(mapped);
      }
    } catch (err) {
      console.error('Login failed:', err);
      const message = err instanceof Error ? err.message : String(err);
      const isNetworkError =
        /failed to fetch|networkerror|load failed|network request failed/i.test(message);
      if (isNetworkError) {
        setError(
          'Không kết nối được máy chủ. Nếu dùng app APK, cần thêm https://localhost vào CORS_ALLOWED_ORIGINS trên Railway.',
        );
      } else if (/sai thông tin|incorrect|unauthorized|401/i.test(message)) {
        setError('Email hoặc mật khẩu không đúng');
      } else {
        setError(message || 'Đăng nhập thất bại');
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loginWithAccessToken = useCallback(async (token: string) => {
    try {
      setIsLoading(true);
      setError(null);
      authApi.setToken(token);
      const me = await authApi.me();
      try {
        const profile = await profileApi.getProfile();
        setUser(mapUser(me, undefined, profile));
      } catch (_) {
        setUser(mapUser(me));
      }
    } catch (err) {
      authApi.clearToken();
      const message = err instanceof Error ? err.message : String(err);
      setError(message || 'Đăng nhập thất bại');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loginWithGoogle = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      if (!clientId) {
        throw new Error('Google Client ID not configured');
      }

      // Load Google SDK
      try {
        await authApi.loadGoogleSignIn();
      } catch (e) {
        console.warn('Google SDK already loaded');
      }

      // Use Google One Tap UI
      return new Promise<void>((resolve, reject) => {
        if (typeof window !== 'undefined' && (window as any).google) {
          try {
            // Initialize with callback
            (window as any).google.accounts.id.initialize({
              client_id: clientId,
              callback: async (response: any) => {
                try {
                  if (!response.credential) {
                    throw new Error('No credential received from Google');
                  }

                  // Send the ID token to backend for authentication
                  await authApi.googleLogin(response.credential);
                  
                  // Fetch user info
                  const me = await authApi.me();
                  try {
                    const profile = await profileApi.getProfile();
                    setUser(mapUser(me, undefined, profile));
                  } catch (_) {
                    setUser(mapUser(me));
                  }
                  
                  navigate('/profile');
                  resolve();
                } catch (err: any) {
                  console.error('Google login error:', err);
                  setError(err.message || 'Google login failed');
                  reject(err);
                } finally {
                  setIsLoading(false);
                }
              },
            });

            // Show Google One Tap UI
            try {
              (window as any).google.accounts.id.prompt((notification: any) => {
                console.log('Google prompt result:', notification);
              });
              console.log('✅ Google One Tap UI shown');
            } catch (e) {
              console.warn('One Tap UI not available, will show alternative');
              // If One Tap fails, show renderButton on a temporary container
              const tempContainer = document.createElement('div');
              tempContainer.id = 'temp_google_container';
              document.body.appendChild(tempContainer);
              (window as any).google.accounts.id.renderButton(tempContainer, {
                type: 'standard',
                theme: 'outline',
                size: 'large',
                text: 'signin_with',
              });
            }
          } catch (e: any) {
            console.error('❌ Google Sign-In initialization failed:', e);
            reject(e);
          }
        } else {
          reject(new Error('Google SDK not loaded'));
        }
      });
    } catch (err: any) {
      console.error('Google login failed:', err);
      setError(err.message || 'Google login failed');
      setIsLoading(false);
      throw err;
    }
  }, [navigate]);

  const register = useCallback(async (data: RegisterData) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await authApi.register({
        email: data.email,
        username: data.email.split('@')[0],
        full_name: data.name,
        phone: data.phone,
        password: data.password,
        account_type: data.accountType,
      });
      return response;
    } catch (err: any) {
      console.error('Registration failed:', err);
      
      // Parse error message to make it more user-friendly
      let errorMessage = err.message || 'Đăng ký thất bại. Vui lòng thử lại.';
      
      // Format validation errors
      if (errorMessage.includes('|')) {
        // Multiple errors separated by |
        const errors = errorMessage.split('|').map((e: string) => e.trim());
        errorMessage = 'Mật khẩu không hợp lệ:\n' + errors.map((e: string) => `• ${e}`).join('\n');
      } else if (errorMessage.includes('\n')) {
        // Multiple errors on separate lines
        const errors = errorMessage.split('\n').filter((e: string) => e.trim());
        errorMessage = errors.map((e: string) => `• ${e.trim()}`).join('\n');
      }
      
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      const wasTenant = user?.accountType === 'tenant';
      const tenantUserId = user?.id;
      if (isNativeMobileApp()) {
        const { unregisterMobilePushToken } = await import('../utils/mobilePushNotifications');
        const { clearBiometricCredentials } = await import('../utils/mobileBiometricLogin');
        await unregisterMobilePushToken();
        await clearBiometricCredentials();
        if (wasTenant) {
          clearTenantMobileLoginData(tenantUserId);
        } else {
          clearPersistedTenantMobileSession();
        }
      }
      authApi.clearToken();
      setUser(null);
      if (isNativeMobileApp() && wasTenant) {
        requestTenantQrLoginScreen();
      }
      navigate(isNativeMobileApp() ? MOBILE_APP_LOGIN_PATH : '/', { replace: true });
    } catch (err) {
      console.error('Logout failed:', err);
      setError('Đăng xuất thất bại. Vui lòng thử lại.');
    } finally {
      setIsLoading(false);
    }
  }, [navigate, user?.accountType, user?.id]);

  const invalidateTenantSession = useCallback(
    async (message?: string) => {
      const tenantUserId = user?.id;
      clearTenantMobileLoginData(tenantUserId);
      authApi.clearToken();
      setUser(null);
      requestTenantQrLoginScreen();
      if (message) {
        setError(message);
      }
      navigate(MOBILE_APP_LOGIN_PATH, { replace: true });
    },
    [navigate, user?.id],
  );

  const updateProfile = useCallback(async (data: Partial<User>) => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('Updating profile with data:', data);
      
      // Helper function to convert empty string to null and ensure value is sent
      const normalizeValue = (value: any) => {
        if (value === undefined) return undefined;
        if (typeof value === 'string' && value.trim() === '') return null;
        return value;
      };
      
      // Map frontend User fields to backend API fields
      // Always include fields that are being updated, even if they are null (to clear them)
      const profileUpdateData: any = {};
      if (data.name !== undefined) {
        profileUpdateData.full_name = normalizeValue(data.name);
        console.log('Setting full_name:', profileUpdateData.full_name);
      }
      if (data.phone !== undefined) {
        profileUpdateData.phone = normalizeValue(data.phone);
        console.log('Setting phone:', profileUpdateData.phone);
      }
      if (data.address !== undefined) {
        profileUpdateData.address = normalizeValue(data.address);
        console.log('Setting address:', profileUpdateData.address);
      }
      if (data.cccd !== undefined) {
        profileUpdateData.cccd = normalizeValue(data.cccd);
      }
      if (data.dateOfBirth !== undefined) {
        profileUpdateData.date_of_birth = normalizeValue(data.dateOfBirth);
      }
      if (data.bio !== undefined) {
        profileUpdateData.bio = normalizeValue(data.bio);
        console.log('Setting bio:', profileUpdateData.bio);
      }
      if (data.company !== undefined) {
        profileUpdateData.company = normalizeValue(data.company);
        console.log('Setting company:', profileUpdateData.company);
      }
      if (data.website !== undefined) {
        profileUpdateData.website = normalizeValue(data.website);
        console.log('Setting website:', profileUpdateData.website);
      }
      if (data.license !== undefined) {
        profileUpdateData.license = normalizeValue(data.license);
        console.log('Setting license:', profileUpdateData.license);
      }
      if (data.taxId !== undefined) profileUpdateData.tax_id = normalizeValue(data.taxId);
      if (data.licenseNumber !== undefined) profileUpdateData.license_number = normalizeValue(data.licenseNumber);
      if (data.specialization !== undefined) profileUpdateData.specialization = normalizeValue(data.specialization);
      if (data.experience !== undefined) profileUpdateData.experience = normalizeValue(data.experience);
      if (data.businessType !== undefined) profileUpdateData.business_type = normalizeValue(data.businessType);
      if (data.industry !== undefined) profileUpdateData.industry = normalizeValue(data.industry);
      if (data.employeeCount !== undefined) profileUpdateData.employee_count = normalizeValue(data.employeeCount);
      
      console.log('Profile update payload (full):', JSON.stringify(profileUpdateData, null, 2));
      
      // Call backend API to update profile
      const updatedProfile = await profileApi.updateProfile(profileUpdateData);
      console.log('Backend response:', updatedProfile);
      
      // Fetch fresh profile data and update context
      const freshProfile = await profileApi.getProfile();
      const me = await authApi.me();
      setUser(mapUser(me, user?.accountType || 'tenant', freshProfile));
      
    } catch (err: any) {
      console.error('Profile update failed:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Cập nhật thông tin thất bại. Vui lòng thử lại.';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [user?.accountType]);

  const switchAccount = useCallback(async (accountType: AccountType): Promise<string> => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('Switching to:', accountType);
      
      // Gọi API để switch account trong database
      await authApi.switchAccount(accountType);
      
      // Refresh user data từ API để lấy account_type và subscription_level cho tài khoản mới
      const me = await authApi.me();
      try {
        const profile = await profileApi.getProfile();
        setUser(mapUser(me, accountType, profile));
      } catch (_) {
        setUser(mapUser(me, accountType));
      }
      
      const targetRoute = `/profile/${accountType}`;
      console.log('Navigating to:', targetRoute);
      navigate(targetRoute, { replace: true });
      return targetRoute;
    } catch (err: any) {
      console.error('Switch account failed:', err);
      const errorMessage = err.message || 'Chuyển đổi tài khoản thất bại. Vui lòng thử lại.';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const forgotPassword = useCallback(async (email: string) => {
    try {
      setIsLoading(true);
      setError(null);
      await authApi.forgotPassword({ email });
    } catch (err: any) {
      console.error('Forgot password failed:', err);
      setError(err.message || 'Có lỗi xảy ra khi gửi email đặt lại mật khẩu');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const resetPassword = useCallback(async (token: string, newPassword: string) => {
    try {
      setIsLoading(true);
      setError(null);
      await authApi.resetPassword({ token, new_password: newPassword });
    } catch (err: any) {
      console.error('Reset password failed:', err);
      setError(err.message || 'Có lỗi xảy ra khi đặt lại mật khẩu');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    try {
      setIsLoading(true);
      setError(null);
      await authApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
    } catch (err: any) {
      console.error('Change password failed:', err);
      setError(err.message || 'Có lỗi xảy ra khi đổi mật khẩu');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const submitKYC = useCallback(async (documents: any, accountType?: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await submitKYCApi(documents);
      console.log('KYC submitted successfully:', result);
      
      const finalAccountType = (accountType as AccountType) || documents?.accountType || user?.accountType || 'tenant';
      if (finalAccountType !== 'tenant') {
        try {
          localStorage.setItem(KYC_TARGET_STORAGE_KEY, finalAccountType);
        } catch (_) { /* ignore */ }
      }
      
      // Fetch fresh user data to get updated KYC status
      const me = await authApi.me();
      const freshProfile = await profileApi.getProfile();
      // The profile endpoint can still expose the previous active account while KYC is pending.
      // Keep the account type selected for this KYC flow so the user remains on the provider profile.
      setUser(mapUser(me, finalAccountType, { ...freshProfile, account_type: finalAccountType }));
      
      return result;
    } catch (err: any) {
      console.error('KYC submission failed:', err);
      const detail = err.response?.data?.detail;
      const errorMessage = Array.isArray(detail)
        ? detail.map((item: any) => item?.msg || item).join(', ')
        : (detail || err.message || 'Có lỗi xảy ra khi gửi KYC');
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const me = await authApi.me();
      const freshProfile = await profileApi.getProfile();
      const mapped = mapUser(me, user?.accountType || 'tenant', freshProfile);
      if (mapped.accountType === 'tenant' && mapped.isVerified && !isNativeMobileApp()) {
        const target = await resolveVerifiedProviderTarget();
        if (target) {
          try {
            await authApi.switchAccount(target);
            localStorage.removeItem(KYC_TARGET_STORAGE_KEY);
            const freshMe = await authApi.me();
            const freshProfileAfterSwitch = await profileApi.getProfile();
            setUser(mapUser(freshMe, target, freshProfileAfterSwitch));
            return;
          } catch (switchErr) {
            console.warn('Auto switch on refresh failed:', switchErr);
          }
        }
      }
      setUser(mapped);
    } catch (err: any) {
      console.error('Refresh user failed:', err);
    }
  }, [user?.accountType]);

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    loginWithAccessToken,
    loginWithGoogle,
    register,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
    updateProfile,
    switchAccount,
    submitKYC,
    refreshUser,
    clearError,
    invalidateTenantSession,
  };


  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Create hook for using auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Safe version that doesn't throw, useful during HMR or conditional rendering
export const useAuthSafe = () => {
  const context = useContext(AuthContext);
  return context || null;
};

export default AuthProvider; 